import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import conf from "../../conf";
import { IDP } from "../auth/AuthProvider";
import ErrorChip from "../utils/ErrorChip";
import { IdpConfig } from "../../hooks/confg/base";

const ProviderSelect = (props: {
  idp: IdpConfig[];
  setIdp: React.Dispatch<React.SetStateAction<IdpConfig[] | []>>;
  disabled?: boolean;
}) => {
  const { disabled, idp, setIdp } = props;

  const [error, setError] = useState(false);
  const [current, setCurrent] = useState<IDP | "">("");
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [authority, setAuthority] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [enableFields, setEnableFields] = useState(false);

  const handleReset = () => {
    setCurrent("");
    setAuthority("");
    setTenantId("");
    setClientId("");
    setClientSecret("");
  };

  const remove = (name: IDP) => {
    const updated = idp.filter((idp) => idp.name !== name);
    setIdp(updated);
  };

  const Error = () => (
    <ErrorChip
      hideIcon
      label={`${current} identity provider already added.`}
      info={
        "To modify an existing identity provider configuration please remove & resubmit all required fields ."
      }
      onClose={() => setError(false)}
    />
  );

  const handleConfirm = () => {
    const config: IdpConfig = {
      name: current as IDP,
      tenantId,
      clientId,
      authority,
      clientSecret,
    };

    const exists = idp.some((idp) => idp.name === config.name);

    if (!exists) {
      // console.log("adding to idp:", config);

      setIdp([...idp, config]);

      // once added reset fields
      handleReset();
    } else {
      // console.log("config found in idp");

      setError(true);
    }
  };

  const handleChange = (event: SelectChangeEvent) =>
    setCurrent(event.target.value as IDP);

  useEffect(() => {
    setEnableFields(!!current);
  }, [current]);

  return (
    <Box sx={{ minWidth: 120 }}>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="idp-select" size="small">
              Name
            </InputLabel>

            <Select
              fullWidth
              size="small"
              id="idp-select"
              labelId="idp-select"
              label="Name"
              value={current}
              disabled={disabled}
              onChange={handleChange}
            >
              {conf.IDPS.map((props) => (
                <MenuItem
                  key={props.value}
                  value={props.value}
                  disabled={props.disabled}
                  // sx={{ display: props.hidden ? "none" : "inherit" }}
                >
                  {props.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Button
            fullWidth
            variant="outlined"
            disabled={!enableFields || disabled}
            onClick={handleReset}
          >
            Reset
          </Button>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Button
            fullWidth
            color="success"
            variant="contained"
            disabled={
              !enableFields ||
              !authority ||
              !tenantId ||
              !clientId ||
              !clientSecret ||
              disabled
            }
            onClick={handleConfirm}
          >
            Add
          </Button>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="organization-idp-authority"
            name="organization-idp-authority"
            size="small"
            label="Authority"
            value={authority}
            disabled={!enableFields || disabled}
            onChange={(e: any) => setAuthority(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="organization-idp-tenant-id"
            name="organization-idp-tenant-id"
            size="small"
            label="Tenant ID"
            value={tenantId}
            disabled={!enableFields || disabled}
            onChange={(e: any) => setTenantId(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="organization-idp-client-id"
            name="organization-idp-client-id"
            size="small"
            label="Client ID"
            value={clientId}
            disabled={!enableFields || disabled}
            onChange={(e: any) => setClientId(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="organization-idp-client-secret"
            name="organization-idp-client-secret"
            size="small"
            label="Client Secret"
            value={clientSecret}
            disabled={!enableFields || disabled}
            onChange={(e: any) => setClientSecret(e.target.value)}
          />
        </Grid>

        <Stack direction="row" spacing={1} m={2} flexWrap="wrap">
          {error && <Error />}
        </Stack>
      </Grid>

      {idp.map((idp) => (
        <Grid container spacing={2} key={idp.tenantId} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption">{idp.name}</Typography>
          </Grid>

          <Grid item xs={12} sm={6} textAlign="end">
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={disabled}
              onClick={() => remove(idp.name)}
            >
              Delete
            </Button>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              disabled
              required
              fullWidth
              id="organization-idp-authority"
              name="organization-idp-authority"
              size="small"
              label="Authority"
              value={idp.authority}
              onChange={(e: any) => setAuthority(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              disabled
              required
              fullWidth
              id="organization-idp-tenant-id"
              name="organization-idp-tenant-id"
              size="small"
              label="Tenant ID"
              value={idp.tenantId}
              onChange={(e: any) => setTenantId(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              disabled
              required
              fullWidth
              id="organization-idp-client-id"
              name="organization-idp-client-id"
              size="small"
              label="Client ID"
              value={idp.clientId}
              onChange={(e: any) => setClientId(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required
              disabled
              fullWidth
              id="organization-idp-client-secret"
              name="organization-idp-client-secret"
              size="small"
              type="password"
              label="Client Secret"
              value={idp.clientSecret}
              onChange={(e: any) => setClientSecret(e.target.value)}
            />
          </Grid>
        </Grid>
      ))}
    </Box>
  );
};
export default ProviderSelect;
